/**
 * Collision edge values for positioning calculations
 * @public
 */
export const CollisionEdges = {
    top: "top",
    right: "right",
    bottom: "bottom",
    left: "left"
};
/**
 * Reposition modes for positioned elements
 * @public
 */
export const RepositionModes = {
    auto: "auto",
    flip: "flip",
    none: "none"
};
/**
 * Default root margin for positioning calculations
 * @internal
 */
export const DEFAULT_ROOT_MARGIN = 15;
/**
 * Gets the boundary bounds for overflow calculations.
 * Uses custom overflow boundary if provided, otherwise uses viewport.
 * @param overflowBoundaryReference - Optional custom boundary element
 * @returns BoundaryBounds object with top, bottom, left, right values
 * @public
 */
export function getBoundaryBounds(overflowBoundaryReference) {
    if (overflowBoundaryReference) {
        const boundaryRect = overflowBoundaryReference.getBoundingClientRect();
        return {
            top: boundaryRect.top,
            bottom: boundaryRect.bottom,
            left: boundaryRect.left,
            right: boundaryRect.right
        };
    }
    return {
        top: 0,
        bottom: window.innerHeight,
        left: 0,
        right: window.innerWidth
    };
}
/**
 * Calculates available space in each direction relative to an anchor element
 * @param anchorRect - The anchor element's bounding rect
 * @param boundary - The boundary bounds
 * @param rootMargin - Margin to subtract from available space
 * @returns Object with available space in each direction
 * @public
 */
export function getAvailableSpace(anchorRect, boundary, rootMargin = DEFAULT_ROOT_MARGIN) {
    return {
        above: anchorRect.top - boundary.top - rootMargin,
        below: boundary.bottom - anchorRect.bottom - rootMargin,
        start: anchorRect.left - boundary.left - rootMargin,
        end: boundary.right - anchorRect.right - rootMargin
    };
}
/**
 * Detects which edge(s) of a boundary the target element is colliding with
 * based on IntersectionObserver entry
 * @param entry - The intersection observer entry
 * @returns The collision edge if any
 * @public
 */
export function detectCollisionEdgeFromIntersection(entry) {
    const targetRect = entry.boundingClientRect;
    const rootRect = entry.rootBounds;
    if (!rootRect) {
        return undefined;
    }
    // Check vertical collisions first (more common for menus/popovers)
    if (targetRect.bottom > rootRect.bottom) {
        return CollisionEdges.bottom;
    }
    if (targetRect.top < rootRect.top) {
        return CollisionEdges.top;
    }
    // Check horizontal collisions
    if (targetRect.left < rootRect.left) {
        return CollisionEdges.left;
    }
    if (targetRect.right > rootRect.right) {
        return CollisionEdges.right;
    }
    return undefined;
}
/**
 * Detects collision by comparing target rect against boundary bounds directly.
 * Useful when IntersectionObserver cannot be used (e.g., popovers in top layer)
 * @param targetRect - The target element's bounding rect
 * @param boundary - The boundary bounds to check against
 * @returns CollisionCheckResult with hasOverflow flag and collision edge
 * @public
 */
export function detectCollisionWithBoundary(targetRect, boundary) {
    const overflowsBottom = targetRect.bottom > boundary.bottom;
    const overflowsTop = targetRect.top < boundary.top;
    const overflowsLeft = targetRect.left < boundary.left;
    const overflowsRight = targetRect.right > boundary.right;
    if (!overflowsBottom && !overflowsTop && !overflowsLeft && !overflowsRight) {
        return {
            hasOverflow: false,
            collisionEdge: undefined
        };
    }
    // Determine collision edge (prioritize vertical over horizontal)
    let collisionEdge;
    if (overflowsBottom) {
        collisionEdge = CollisionEdges.bottom;
    }
    else if (overflowsTop) {
        collisionEdge = CollisionEdges.top;
    }
    else if (overflowsLeft) {
        collisionEdge = CollisionEdges.left;
    }
    else if (overflowsRight) {
        collisionEdge = CollisionEdges.right;
    }
    return {
        hasOverflow: true,
        collisionEdge
    };
}
/**
 * Gets the opposite vertical edge
 * @param edge - The collision edge
 * @returns The opposite edge for vertical positions
 * @public
 */
export function getOppositeVerticalEdge(edge) {
    if (edge === CollisionEdges.top) {
        return CollisionEdges.bottom;
    }
    if (edge === CollisionEdges.bottom) {
        return CollisionEdges.top;
    }
    return edge;
}
/**
 * Gets the opposite horizontal edge
 * @param edge - The collision edge
 * @returns The opposite edge for horizontal positions
 * @public
 */
export function getOppositeHorizontalEdge(edge) {
    if (edge === CollisionEdges.left) {
        return CollisionEdges.right;
    }
    if (edge === CollisionEdges.right) {
        return CollisionEdges.left;
    }
    return edge;
}
/**
 * Checks if a target element fits within the available space
 * @param targetSize - Width or height of the target element
 * @param availableSpace - Available space in the direction
 * @returns Whether the target fits
 * @public
 */
export function fitsInSpace(targetSize, availableSpace) {
    return targetSize <= availableSpace;
}
//# sourceMappingURL=positioning.js.map